/**
 * Server state for build step 4 — timesheets (the gate, the manager grid, analysis, feedback, automation
 * candidates, nudges) and the HR holiday calendar with optional holidays. Every read and write goes through
 * a contract route (packages/contracts routes.step4); the SPA never recomputes working days itself — it
 * reads GET /working-days and the `expected_hours` the API puts on every cell.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { AutomationCandidate, RouteQuery, TimesheetFeedback, TimesheetsResponse } from '@amplispot/contracts';
import { call } from '../api';
import { addDays } from './work';

export const tkeys = {
  all: ['ts'] as const,
  grid: (view: string, from: string, to: string) => ['ts', 'grid', view, from, to] as const,
  workingDays: (from: string, to: string, userId?: string) => ['ts', 'wd', from, to, userId ?? 'me'] as const,
  gate: ['ts', 'gate'] as const,
  feedback: (period?: string) => ['ts', 'feedback', period ?? ''] as const,
  automation: (period: string) => ['ts', 'automation', period] as const,
  myOptional: (year: number) => ['ts', 'my-optional', year] as const,
  optionalAdmin: (year: number) => ['ts', 'optional-admin', year] as const,
  plans: (date: string, ids: string) => ['ts', 'plans', date, ids] as const,
  /** Under ['settings'] on purpose: every settings write (thresholds, flags, working day) bumps the one shared
   *  config_version, and useSettingsRefresh invalidates ['settings'], so this If-Match version never goes stale
   *  (review t-02be16fa). */
  workingDaySetting: ['settings', 'working-day'] as const,
};
export const hkeys = {
  all: ['holidays'] as const,
  list: ['holidays', 'list'] as const,
  cal: (id: string) => ['holidays', 'cal', id] as const,
  preview: (id: string) => ['holidays', 'preview', id] as const,
  types: ['holidays', 'types'] as const,
};

/** A timesheet write can change the gate, /me (gate + badges), Today, the grid and analysis. */
export function refreshTimesheets(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: tkeys.all });
  void qc.invalidateQueries({ queryKey: ['me'] });
  void qc.invalidateQueries({ queryKey: ['work'] });
}
/** A published calendar changes every working-day number on every screen. */
export function refreshHolidays(qc: QueryClient) {
  // Not the publish preview: after a publish it would re-ask about a calendar that is no longer a draft (409).
  void qc.invalidateQueries({ queryKey: hkeys.all, predicate: (q) => q.queryKey[1] !== 'preview' });
  qc.removeQueries({ queryKey: ['holidays', 'preview'] });
  refreshTimesheets(qc);
}

type Paged<T> = { data: T[]; next_cursor: string | null };
async function allPages<T>(fetchPage: (cursor: string | undefined) => Promise<Paged<T>>): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | undefined;
  do {
    const p = await fetchPage(cursor);
    out.push(...p.data);
    cursor = p.next_cursor ?? undefined;
  } while (cursor);
  return out;
}

// ---------------------------------------------------------------- dates and periods

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const d0 = (iso: string) => new Date(`${iso}T00:00:00Z`);

/** "Fri 2 Oct" — the prototype's day label. */
export const shortDay = (iso: string) => {
  const d = d0(iso);
  return `${DOW[d.getUTCDay()]} ${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
};
/** "2 Oct" */
export const dayMonth = (iso: string) => {
  const d = d0(iso);
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
};
export const mondayOf = (iso: string) => {
  const w = d0(iso).getUTCDay();
  return addDays(iso, w === 0 ? -6 : 1 - w);
};
/** Prototype period label for the week picker and grid headers: "Week of 28 Sep". */
export const weekLabel = (monday: string) => `Week of ${dayMonth(monday)}`;
/** The `period` stored on feedback for a week — year-unique (review t-fdf941aa), so next year's "Week of 12 Oct"
 *  never picks up this year's notes: "Week of 12 Oct 2026". */
export const weekPeriod = (monday: string) => `${weekLabel(monday)} ${monday.slice(0, 4)}`;
export const monthStart = (iso: string) => `${iso.slice(0, 7)}-01`;
export const monthEnd = (iso: string) => {
  const d = d0(monthStart(iso));
  d.setUTCMonth(d.getUTCMonth() + 1);
  d.setUTCDate(0);
  return d.toISOString().slice(0, 10);
};
/** "September 2026" — the current month reads "October 2026 so far" (prototype). */
export const monthLabel = (iso: string, today: string) => {
  const d = d0(iso);
  const base = `${MONTH[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  return iso.slice(0, 7) === today.slice(0, 7) ? `${base} so far` : base;
};
/** Feedback periods are stored without "so far", so a month's notes stay together after it ends. */
export const monthPeriod = (iso: string) => {
  const d = d0(iso);
  return `${MONTH[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};
export const lastWeeks = (today: string, n: number) => Array.from({ length: n }, (_, i) => addDays(mondayOf(today), -7 * i));
export const lastMonths = (today: string, n: number) =>
  Array.from({ length: n }, (_, i) => {
    const d = d0(monthStart(today));
    d.setUTCMonth(d.getUTCMonth() - i);
    return d.toISOString().slice(0, 10);
  });

export const r1 = (v: number) => Math.round(v * 10) / 10;
export const fh = (v: number) => `${r1(v)} h`;
/** "6:20 pm" in Asia/Kolkata from a timestamp. */
export const istTime = (ts: string) =>
  new Date(ts).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();

// ---------------------------------------------------------------- reads

export function useTimesheetGrid(view: 'day' | 'week' | 'month', from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: tkeys.grid(view, from, to),
    queryFn: (): Promise<TimesheetsResponse> => call('timesheets.grid', { query: { view, from, to } }),
    enabled,
  });
}

export function useWorkingDays(from: string, to: string, userId?: string, enabled = true) {
  return useQuery({
    queryKey: tkeys.workingDays(from, to, userId),
    queryFn: () => call('workingDays.get', { query: { from, to, ...(userId ? { user_id: userId } : {}) } }),
    enabled,
  });
}

export function useGate(enabled = true) {
  return useQuery({ queryKey: tkeys.gate, queryFn: () => call('me.timesheetGate'), enabled });
}

/** Plan confirmation per person for one day (Team → Timesheets, Day view: "Plan confirmed"). */
export function usePlansFor(date: string, userIds: string[], enabled = true) {
  return useQuery({
    queryKey: tkeys.plans(date, userIds.join(',')),
    queryFn: async () => {
      const out = new Map<string, string | null>();
      await Promise.all(
        userIds.map(async (id) => {
          try {
            const p = await call('me.plan', { query: { user_id: id, date } });
            out.set(id, p.confirmed_at);
          } catch {
            out.set(id, null);
          }
        }),
      );
      return out;
    },
    enabled: enabled && userIds.length > 0,
  });
}

export function useFeedback(enabled = true) {
  return useQuery({
    queryKey: tkeys.feedback(),
    queryFn: (): Promise<TimesheetFeedback[]> =>
      allPages((cursor) => call('feedback.list', { query: { limit: 100, cursor } as RouteQuery<'feedback.list'> })),
    enabled,
  });
}

/** Settings → the hours of a full working day (GET /settings/working-day; decision 9). */
export function useWorkingDaySetting(enabled = true) {
  return useQuery({ queryKey: tkeys.workingDaySetting, queryFn: () => call('settings.workingDay'), enabled });
}
/** PATCH with If-Match = config_version; every expected-hours number moves, so the timesheet views and /me refresh. */
export function useUpdateWorkingDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { version: number; hours: number }) => call('settings.updateWorkingDay', { body: { hours: v.hours }, ifMatch: v.version }),
    onSuccess: () => {
      refreshTimesheets(qc);
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

export function useAutomation(period: string, enabled = true) {
  return useQuery({
    queryKey: tkeys.automation(period),
    queryFn: (): Promise<AutomationCandidate[]> =>
      allPages((cursor) => call('automation.list', { query: { period, limit: 100, cursor } as RouteQuery<'automation.list'> })),
    enabled,
  });
}

export function useMyOptional(year: number, enabled = true) {
  return useQuery({ queryKey: tkeys.myOptional(year), queryFn: () => call('me.optionalHolidays', { query: { year } }), enabled });
}
export function useOptionalAdmin(year: number, enabled = true) {
  return useQuery({ queryKey: tkeys.optionalAdmin(year), queryFn: () => call('optionalHolidays.admin', { query: { year } }), enabled });
}

export function useCalendars() {
  return useQuery({ queryKey: hkeys.list, queryFn: () => call('holidays.list', { query: {} }) });
}
export function useCalendar(id: string | null) {
  return useQuery({ queryKey: hkeys.cal(id ?? ''), queryFn: () => call('holidays.get', { params: { id: id! } }), enabled: !!id });
}
export function useDayTypes() {
  return useQuery({ queryKey: hkeys.types, queryFn: () => call('holidayTypes.list') });
}

// ---------------------------------------------------------------- writes

/** Any timesheet write: refreshes the gate, /me, Today and every grid on success. */
export function useTsWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => refreshTimesheets(qc) });
}
export function useHolidayWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => refreshHolidays(qc) });
}
